import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';

import { SaveOutlined, CancelOutlined } from '@mui/icons-material';
import { extractErrorMessage } from '../utils/errorUtils';
import { useClientStore } from '../stores/useClientStore';
import { Client } from '../types';
import AddressAutocomplete from '../components/AddressAutocomplete';

type ClientFormData = Omit<Client, 'id' | 'createdAt' | 'updatedAt'>;

const ClientFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const { addClient, updateClient, getClientById, isLoading, error } = useClientStore();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ClientFormData>({
    defaultValues: {
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      adresse: '',
      ville: '',
      codePostal: '',
      siren: '',
      codePaysIso: 'FR',
      numTvaIntracom: '',
    },
  });

  // Charger le client en mode édition
  useEffect(() => {
    if (isEditing && id) {
      const clientId = parseInt(id, 10);
      const client = getClientById(clientId);
      if (client) {
        reset({
          nom: client.nom,
          prenom: client.prenom,
          email: client.email,
          telephone: client.telephone,
          adresse: client.adresse,
          ville: client.ville,
          codePostal: client.codePostal,
          siren: client.siren || '',
          codePaysIso: client.codePaysIso || 'FR',
          numTvaIntracom: client.numTvaIntracom || '',
        });
      }
    }
  }, [isEditing, id, getClientById, reset]);

  const onSubmit = async (data: ClientFormData) => {
    setSubmitError(null);
    try {
      if (isEditing && id) {
        await updateClient(parseInt(id, 10), data);
      } else {
        await addClient(data);
      }
      navigate('/clients');
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de l\'enregistrement du client');
      setSubmitError(errorMessage);
    }
  };

  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: 700, color: '#333', mb: 3 }}
      >
        {isEditing ? 'Modifier le client' : 'Nouveau client'}
      </Typography>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        {(submitError || error) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError || error}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={{ xs: 3, sm: 2 }}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="prenom"
                control={control}
                rules={{ required: 'Le prénom est requis' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Prénom"
                    error={!!errors.prenom}
                    helperText={errors.prenom?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="nom"
                control={control}
                rules={{ required: 'Le nom est requis' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Nom"
                    error={!!errors.nom}
                    helperText={errors.nom?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: "L'email est requis",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Email invalide',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Email"
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="telephone"
                control={control}
                rules={{ required: 'Le téléphone est requis' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Téléphone"
                    type="tel"
                    error={!!errors.telephone}
                    helperText={errors.telephone?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                      },
                    }}
                    inputProps={{
                      inputMode: 'tel',
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="adresse"
                control={control}
                rules={{ required: "L'adresse est requise" }}
                render={({ field }) => (
                  <AddressAutocomplete
                    label="Adresse *"
                    value={field.value}
                    onChange={field.onChange}
                    onAddressSelect={(address) => {
                      // Remplir automatiquement les champs ville et code postal
                      setValue('ville', address.ville);
                      setValue('codePostal', address.codePostal);
                    }}
                    error={!!errors.adresse}
                    helperText={errors.adresse?.message}
                    placeholder="Ex: 8 Boulevard du Port, Amiens"
                    required
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <Controller
                name="ville"
                control={control}
                rules={{ required: 'La ville est requise' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Ville"
                    error={!!errors.ville}
                    helperText={errors.ville?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="codePostal"
                control={control}
                rules={{ required: 'Le code postal est requis' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Code Postal"
                    error={!!errors.codePostal}
                    helperText={errors.codePostal?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                      },
                    }}
                    inputProps={{
                      inputMode: 'numeric',
                    }}
                  />
                )}
              />
            </Grid>

            {/* Champs Factur-X (conformité e-invoicing 2026) */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 1, sm: 2 }, color: '#374151', fontWeight: 600 }}>
                Informations légales (Factur-X)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="siren"
                control={control}
                rules={{
                  pattern: {
                    value: /^[0-9]{9,14}$/,
                    message: 'SIREN/SIRET invalide (9 ou 14 chiffres)',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="SIREN / SIRET (optionnel)"
                    error={!!errors.siren}
                    helperText={errors.siren?.message || '9 chiffres (SIREN) ou 14 chiffres (SIRET)'}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                      },
                    }}
                    inputProps={{
                      inputMode: 'numeric',
                      maxLength: 14,
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="codePaysIso"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Code Pays ISO"
                    placeholder="FR"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                      },
                    }}
                    inputProps={{
                      maxLength: 2,
                      style: { textTransform: 'uppercase' },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="numTvaIntracom"
                control={control}
                rules={{
                  pattern: {
                    value: /^[A-Z]{2}[0-9A-Z]{2,13}$/,
                    message: 'Format invalide (ex: FR12345678901)',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="N° TVA Intracommunautaire (optionnel)"
                    placeholder="FR12345678901"
                    error={!!errors.numTvaIntracom}
                    helperText={errors.numTvaIntracom?.message || 'Ex: FR12345678901'}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                      },
                    }}
                    inputProps={{
                      maxLength: 15,
                      style: { textTransform: 'uppercase' },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: { xs: 3, sm: 4 } }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveOutlined />}
              sx={{
                py: { xs: 1.8, sm: 1.5 },
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 600,
                bgcolor: '#F59E0B',
                '&:hover': { bgcolor: '#D97706' },
              }}
            >
              {isLoading ? 'Enregistrement...' : (isEditing ? 'Modifier' : 'Enregistrer')}
            </Button>

            <Button
              variant="outlined"
              size="large"
              fullWidth
              startIcon={<CancelOutlined />}
              onClick={() => navigate('/clients')}
              sx={{
                py: { xs: 1.8, sm: 1.5 },
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 600,
              }}
            >
              Annuler
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ClientFormPage;
